/**
 * Firm-side "Magic Links" audit drawer.
 *
 * Lists every magic-login link minted by this firm with:
 *   • Who minted it + when
 *   • Which workspace + user it targets
 *   • TTL in days + absolute expiry
 *   • Redemption status (active / redeemed / expired / revoked)
 *   • Redeemed-at timestamp + IP (compliance trail for lobby QRs)
 *   • One-click Revoke for links still in flight
 */
import { useCallback, useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Shield, ShieldOff, CheckCircle2, Clock, XCircle } from "lucide-react";
import { fmtDate } from "@/lib/format";

const STATUS_META = {
  active:   { label: "Active",   bg: "#D1FAE5", fg: "#065F46", Icon: CheckCircle2 },
  redeemed: { label: "Redeemed", bg: "#DBEAFE", fg: "#1E3A8A", Icon: CheckCircle2 },
  expired:  { label: "Expired",  bg: "#F3F4F6", fg: "#4B5563", Icon: Clock },
  revoked:  { label: "Revoked",  bg: "#FEE2E2", fg: "#991B1B", Icon: XCircle },
};

export default function MagicLinksDrawer({ open, onClose, clientTenantId = null }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const url = clientTenantId
        ? `/firm/magic-links?client_tenant_id=${encodeURIComponent(clientTenantId)}`
        : "/firm/magic-links";
      const { data } = await api.get(url);
      setItems(data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load links");
    } finally { setLoading(false); }
  }, [open, clientTenantId]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (id, label) => {
    if (!window.confirm(`Revoke the link for ${label}? Any outstanding copy / QR will stop working immediately.`)) return;
    setRevoking(id);
    try {
      await api.post(`/firm/magic-links/${id}/revoke`);
      toast.success("Link revoked");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not revoke");
    } finally { setRevoking(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="magic-links-drawer">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Shield size={16} /> Magic sign-in links
          </DialogTitle>
          <DialogDescription className="text-[var(--ll-ink-soft)]">
            Every link minted by your firm — who minted it, who redeemed it, from which IP. Revoke anything still in flight.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="ll-progress my-3" />}

        {!loading && items.length === 0 && (
          <div className="py-12 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="magic-links-empty">
            <Shield size={28} className="mx-auto mb-2 opacity-40" />
            No magic links minted yet. Use the icon on each client card to generate one.
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="magic-links-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] border-b" style={{ borderColor: "var(--ll-border)" }}>
                  <th className="py-2 pr-3 font-medium">Target</th>
                  <th className="py-2 pr-3 font-medium">Minted</th>
                  <th className="py-2 pr-3 font-medium">TTL</th>
                  <th className="py-2 pr-3 font-medium">Redeemed</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 pr-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.active;
                  const StatusIcon = meta.Icon;
                  const canRevoke = r.status === "active" || r.status === "redeemed";
                  return (
                    <tr key={r.id} className="border-b hover:bg-[var(--ll-row-hover)]" style={{ borderColor: "var(--ll-border)" }} data-testid={`magic-link-row-${r.id}`}>
                      <td className="py-2 pr-3">
                        <div className="font-medium">{r.target_user_email}</div>
                        <div className="text-[10px] text-[var(--ll-ink-muted)]">{r.target_tenant_name}</div>
                      </td>
                      <td className="py-2 pr-3">
                        <div>{fmtDate(r.minted_at)}</div>
                        <div className="text-[10px] text-[var(--ll-ink-muted)]">by {r.minted_by_email}</div>
                      </td>
                      <td className="py-2 pr-3">
                        <div>{r.ttl_days}d</div>
                        <div className="text-[10px] text-[var(--ll-ink-muted)]">→ {fmtDate(r.expires_at)}</div>
                      </td>
                      <td className="py-2 pr-3">
                        {r.redeemed_at ? (
                          <>
                            <div>{fmtDate(r.redeemed_at)}</div>
                            <div className="text-[10px] text-[var(--ll-ink-muted)]">
                              {(() => {
                                const locationBits = [r.redeemed_city, r.redeemed_region, r.redeemed_country].filter(Boolean);
                                return locationBits.length > 0 ? locationBits.join(", ") : (r.redeemed_ip || "—");
                              })()}
                            </div>
                            {r.redeemed_city && r.redeemed_ip && (
                              <div className="text-[9px] text-[var(--ll-ink-muted)] font-mono-fin opacity-60" title={r.redeemed_ip}>
                                {r.redeemed_ip}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-[var(--ll-ink-muted)]">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest"
                          style={{ background: meta.bg, color: meta.fg }}
                          data-testid={`magic-link-status-${r.id}`}
                        >
                          <StatusIcon size={10} /> {meta.label}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {canRevoke ? (
                          <button
                            type="button"
                            onClick={() => revoke(r.id, `${r.target_user_email} @ ${r.target_tenant_name}`)}
                            disabled={revoking === r.id}
                            data-testid={`magic-link-revoke-${r.id}`}
                            className="text-[10px] px-2 py-1 border hover:bg-[#FEE2E2] hover:border-[var(--ll-error)] hover:text-[var(--ll-error)] inline-flex items-center gap-1 disabled:opacity-60"
                            style={{ borderColor: "var(--ll-border)" }}
                          >
                            {revoking === r.id ? <Loader2 size={10} className="animate-spin" /> : <ShieldOff size={10} />}
                            Revoke
                          </button>
                        ) : (
                          <span className="text-[10px] text-[var(--ll-ink-muted)]">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
